import React, { useState, useEffect, useCallback } from 'react';
import {
  RotateCw,
  Eye,
  RefreshCcw,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  FileText,
  ArrowDownLeft,
  ArrowUpRight,
  Cpu,
  User,
  ChevronLeft,
  ChevronRight,
  Database,
  Check,
  Copy,
} from 'lucide-react';
import { TableLoader } from '../../components/Common/Loader';
import SapMetricCards from './components/SapMetricCards';
import SapActionToolbar from './components/SapActionToolbar';
import SapLogDetailDrawer from './components/SapLogDetailDrawer';
import TestSftpModal from './components/TestSftpModal';
import TriggerJobModal from './components/TriggerJobModal';
import {
  getSapSyncLogsAPI,
  getSapSyncMetricsAPI,
  retrySapSyncLogAPI,
  triggerSapInboundSyncAPI,
  triggerSapDailyOrdersSyncAPI,
} from '../../api/sapSync';
import type {
  SapSyncLogItem,
  SapSyncMetric,
  SapSyncFilterState,
  SapSyncPagination,
} from '../../types/sapSync';

export const SapSyncDashboard: React.FC = () => {
  // Logs & Metrics state
  const [logs, setLogs] = useState<SapSyncLogItem[]>([]);
  const [metrics, setMetrics] = useState<SapSyncMetric>({});
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [loadingMetrics, setLoadingMetrics] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toastMessage, setToastMessage] = useState<{ text: string; type: 'success' | 'error' } | null>(null);

  // Pagination state
  const [pagination, setPagination] = useState<SapSyncPagination>({
    total: 0,
    page: 1,
    limit: 20,
    totalPages: 1,
  });

  // Filters state
  const [filters, setFilters] = useState<SapSyncFilterState>({
    search: '',
    status: '',
    jobType: '',
    direction: '',
  });

  // Action loadings
  const [loadingInbound, setLoadingInbound] = useState(false);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [retryingLogId, setRetryingLogId] = useState<number | string | null>(null);

  // Modals & Drawer state
  const [selectedLogId, setSelectedLogId] = useState<number | string | null>(null);
  const [isDetailDrawerOpen, setIsDetailDrawerOpen] = useState(false);
  const [isTestSftpOpen, setIsTestSftpOpen] = useState(false);
  const [isTriggerJobOpen, setIsTriggerJobOpen] = useState(false);

  // Copied file state in table
  const [copiedFile, setCopiedFile] = useState<string | null>(null);

  const showToast = (text: string, type: 'success' | 'error' = 'success') => {
    setToastMessage({ text, type });
    setTimeout(() => {
      setToastMessage(null);
    }, 4000);
  };

  // Fetch metrics
  const fetchMetrics = useCallback(async () => {
    setLoadingMetrics(true);
    try {
      const res = await getSapSyncMetricsAPI();
      if (res && res.data) {
        setMetrics(res.data);
      } else if (res) {
        setMetrics(res as any);
      }
    } catch (err: any) {
      console.warn('Could not fetch metrics:', err);
    } finally {
      setLoadingMetrics(false);
    }
  }, []);

  // Fetch logs
  const fetchLogs = useCallback(async () => {
    setLoadingLogs(true);
    setError(null);
    try {
      const res = await getSapSyncLogsAPI({
        page: pagination.page,
        limit: pagination.limit,
        search: filters.search.trim() || undefined,
        status: filters.status || undefined,
        jobType: filters.jobType.trim() || undefined,
        direction: filters.direction || undefined,
        sortBy: 'createdAt',
        sortOrder: 'desc',
      });

      if (res && res.data) {
        setLogs(res.data);
      } else if (Array.isArray(res)) {
        setLogs(res);
      } else {
        setLogs([]);
      }

      if (res && res.pagination) {
        setPagination((prev) => ({
          ...prev,
          total: res.pagination?.total || 0,
          totalPages: res.pagination?.totalPages || 1,
        }));
      }

      // If backend attached metrics directly in logs response
      if (res && res.metrics) {
        setMetrics(res.metrics);
      }
    } catch (err: any) {
      console.error('Failed to fetch SAP logs:', err);
      setError(err.message || 'Failed to load SAP sync logs');
    } finally {
      setLoadingLogs(false);
    }
  }, [pagination.page, pagination.limit, filters]);

  // Initial load
  useEffect(() => {
    fetchMetrics();
  }, [fetchMetrics]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  // Handle Trigger Inbound SFTP
  const handleTriggerInbound = async () => {
    setLoadingInbound(true);
    try {
      const res = await triggerSapInboundSyncAPI();
      showToast(res.message || 'Inbound SFTP scan triggered successfully!', 'success');
      fetchLogs();
      fetchMetrics();
    } catch (err: any) {
      console.error('Trigger inbound error:', err);
      showToast(err.message || 'Failed to trigger Inbound SFTP scan', 'error');
    } finally {
      setLoadingInbound(false);
    }
  };

  // Handle Trigger Daily Orders
  const handleTriggerDailyOrders = async () => {
    setLoadingOrders(true);
    try {
      const res = await triggerSapDailyOrdersSyncAPI();
      showToast(res.message || 'Daily orders sync to SAP dispatched successfully!', 'success');
      fetchLogs();
      fetchMetrics();
    } catch (err: any) {
      console.error('Trigger orders sync error:', err);
      showToast(err.message || 'Failed to trigger daily orders sync', 'error');
    } finally {
      setLoadingOrders(false);
    }
  };

  // Handle 1-Click Retry
  const handleRetryLog = async (id: number | string, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    setRetryingLogId(id);
    try {
      const res = await retrySapSyncLogAPI(id);
      showToast(res.message || `Sync Run #${id} retry dispatched!`, 'success');
      fetchLogs();
      fetchMetrics();
    } catch (err: any) {
      console.error('Retry log error:', err);
      showToast(err.message || `Failed to retry Sync Run #${id}`, 'error');
    } finally {
      setRetryingLogId(null);
    }
  };

  const handleOpenDetails = (id: number | string) => {
    setSelectedLogId(id);
    setIsDetailDrawerOpen(true);
  };

  const handleCopyFileName = (fileName: string, e: React.MouseEvent) => {
    e.stopPropagation();
    navigator.clipboard.writeText(fileName);
    setCopiedFile(fileName);
    setTimeout(() => setCopiedFile(null), 2000);
  };

  const formatDate = (dateStr?: string | null) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateStr;
    }
  };

  const formatDuration = (ms?: number | null, raw?: string | number) => {
    if (raw !== undefined && raw !== null && typeof raw === 'string') return raw;
    if (ms === undefined || ms === null) return '-';
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    const mins = Math.floor(ms / 60000);
    const secs = Math.floor((ms % 60000) / 1000);
    return `${mins}m ${secs}s`;
  };

  return (
    <div className="min-h-screen">
      {/* Toast Notification */}
      {toastMessage && (
        <div className="fixed top-5 right-5 z-50 animate-bounce">
          <div
            className={`px-4 py-3 rounded-xl shadow-lg border flex items-center gap-2.5 text-xs font-semibold ${
              toastMessage.type === 'success'
                ? 'bg-emerald-900 text-emerald-100 border-emerald-700'
                : 'bg-rose-900 text-rose-100 border-rose-700'
            }`}
          >
            {toastMessage.type === 'success' ? (
              <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
            ) : (
              <XCircle className="w-4 h-4 text-rose-400 shrink-0" />
            )}
            <span>{toastMessage.text}</span>
          </div>
        </div>
      )}

      {/* Header Section */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-blue-600 text-white flex items-center justify-center font-bold font-mono text-sm shadow-sm">
              SAP
            </div>
            <div>
              <h1 className="text-xl font-bold text-slate-900">SAP Sync & SFTP Integration</h1>
              <p className="text-xs text-slate-500">
                Internal Ops & Developer Console • Monitor SFTP file exchanges, triggers & logs
              </p>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => {
              fetchLogs();
              fetchMetrics();
            }}
            disabled={loadingLogs || loadingMetrics}
            className="px-3 py-2 bg-white hover:bg-slate-100 text-slate-700 border border-slate-200 rounded-lg text-xs font-semibold shadow-2xs flex items-center gap-1.5 transition-colors"
          >
            <RotateCw className={`w-3.5 h-3.5 ${loadingLogs || loadingMetrics ? 'animate-spin' : ''}`} />
            Refresh Data
          </button>
        </div>
      </div>

      {/* 1. KPI Metric Cards */}
      <SapMetricCards metrics={metrics} loading={loadingMetrics} />

      {/* 2. Action Toolbar & Filter Bar */}
      <SapActionToolbar
        filters={filters}
        onFilterChange={(newFilters) => {
          setFilters((prev) => ({ ...prev, ...newFilters }));
          setPagination((prev) => ({ ...prev, page: 1 }));
        }}
        onResetFilters={() => {
          setFilters({ search: '', status: '', jobType: '', direction: '' });
          setPagination((prev) => ({ ...prev, page: 1 }));
        }}
        onOpenTestSftp={() => setIsTestSftpOpen(true)}
        onTriggerInbound={handleTriggerInbound}
        onTriggerDailyOrders={handleTriggerDailyOrders}
        onOpenTriggerJob={() => setIsTriggerJobOpen(true)}
        loadingInbound={loadingInbound}
        loadingOrders={loadingOrders}
      />

      {/* 3. Sync Logs Table Card */}
      <div className="bg-white rounded-xl border border-slate-200/80 shadow-xs overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Database className="w-4 h-4 text-slate-500" />
            <h3 className="text-sm font-bold text-slate-800">
              SAP Sync Execution History
            </h3>
            <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">
              {pagination.total} Records
            </span>
          </div>
          <span className="text-xs text-slate-400">Click row to inspect error details & JSON</span>
        </div>

        {/* Table Content */}
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs border-collapse">
            <thead className="bg-slate-50/80 border-b border-slate-200 text-slate-600 font-bold uppercase tracking-wider text-[10px]">
              <tr>
                <th className="py-3 px-4">Run ID / Ref</th>
                <th className="py-3 px-4">Job Type</th>
                <th className="py-3 px-4">Direction</th>
                <th className="py-3 px-4">File Name</th>
                <th className="py-3 px-4">Status</th>
                <th className="py-3 px-4 text-center">Records</th>
                <th className="py-3 px-4">Duration</th>
                <th className="py-3 px-4">Triggered By</th>
                <th className="py-3 px-4">Executed At</th>
                <th className="py-3 px-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loadingLogs ? (
                <TableLoader colSpan={10} text="Loading SAP sync logs..." />
              ) : error ? (
                <tr>
                  <td colSpan={10} className="py-10 text-center text-rose-600 font-medium">
                    <AlertTriangle className="w-6 h-6 mx-auto mb-2 text-rose-500" />
                    {error}
                  </td>
                </tr>
              ) : logs.length === 0 ? (
                <tr>
                  <td colSpan={10} className="py-16 text-center text-slate-400">
                    <Database className="w-10 h-10 mx-auto text-slate-300 mb-2" />
                    <p className="text-sm font-semibold text-slate-600">No SAP Sync Logs Found</p>
                    <p className="text-xs text-slate-400 mt-0.5">
                      {filters.search || filters.status || filters.jobType
                        ? 'Try clearing the search or status filters.'
                        : 'Trigger an Inbound Scan or Orders Sync above to start.'}
                    </p>
                  </td>
                </tr>
              ) : (
                logs.map((item) => {
                  const statusNorm = (item.status || '').toUpperCase();
                  const isSuccess = statusNorm === 'SUCCESS' || statusNorm === 'COMPLETED';
                  const isPartial = statusNorm.includes('PARTIAL');
                  const isFailed = statusNorm === 'FAILED' || statusNorm === 'ERROR';

                  const totalRec = item.totalRecords ?? 0;
                  const successRec = item.successRecords ?? item.syncedRecords ?? 0;

                  return (
                    <tr
                      key={item.id}
                      onClick={() => handleOpenDetails(item.id)}
                      className="hover:bg-blue-50/40 cursor-pointer transition-colors group"
                    >
                      {/* 1. Run ID */}
                      <td className="py-3.5 px-4 font-mono font-bold text-slate-800">
                        #{item.runId || item.id}
                      </td>

                      {/* 2. Job Type */}
                      <td className="py-3.5 px-4 font-mono font-semibold text-slate-700">
                        {item.jobType || 'SAP_SYNC'}
                      </td>

                      {/* 3. Direction */}
                      <td className="py-3.5 px-4">
                        <span
                          className={`inline-flex items-center gap-1 font-semibold px-2 py-0.5 rounded-md text-[10px] ${
                            item.direction === 'INBOUND'
                              ? 'bg-blue-50 text-blue-700 border border-blue-200/60'
                              : 'bg-purple-50 text-purple-700 border border-purple-200/60'
                          }`}
                        >
                          {item.direction === 'INBOUND' ? (
                            <ArrowDownLeft className="w-3 h-3" />
                          ) : (
                            <ArrowUpRight className="w-3 h-3" />
                          )}
                          {item.direction || 'INBOUND'}
                        </span>
                      </td>

                      {/* 4. File Name */}
                      <td className="py-3.5 px-4">
                        {item.fileName ? (
                          <div className="flex items-center gap-1.5 max-w-[200px]">
                            <FileText className="w-3.5 h-3.5 text-blue-500 shrink-0" />
                            <span
                              className="font-mono text-slate-700 truncate"
                              title={item.fileName}
                            >
                              {item.fileName}
                            </span>
                            <button
                              onClick={(e) => handleCopyFileName(item.fileName!, e)}
                              className="p-1 hover:bg-slate-200 rounded text-slate-400 hover:text-slate-700 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
                              title="Copy file name"
                            >
                              {copiedFile === item.fileName ? (
                                <Check className="w-3 h-3 text-emerald-600" />
                              ) : (
                                <Copy className="w-3 h-3" />
                              )}
                            </button>
                          </div>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </td>

                      {/* 5. Status Badge */}
                      <td className="py-3.5 px-4">
                        <span
                          className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-bold ${
                            isSuccess
                              ? 'bg-emerald-100 text-emerald-800'
                              : isPartial
                              ? 'bg-amber-100 text-amber-800'
                              : isFailed
                              ? 'bg-rose-100 text-rose-800'
                              : 'bg-blue-100 text-blue-800'
                          }`}
                        >
                          {isSuccess ? (
                            <CheckCircle2 className="w-3 h-3" />
                          ) : isPartial ? (
                            <AlertTriangle className="w-3 h-3" />
                          ) : isFailed ? (
                            <XCircle className="w-3 h-3" />
                          ) : (
                            <RotateCw className="w-3 h-3 animate-spin" />
                          )}
                          {item.status || 'UNKNOWN'}
                        </span>
                      </td>

                      {/* 6. Records */}
                      <td className="py-3.5 px-4 text-center font-mono font-semibold text-slate-700">
                        {totalRec > 0 ? `${successRec} / ${totalRec}` : `${successRec} / ${totalRec}`}
                      </td>

                      {/* 7. Duration */}
                      <td className="py-3.5 px-4 text-slate-600 font-mono">
                        {formatDuration(item.durationMs ?? item.executionDurationMs, item.executionDuration)}
                      </td>

                      {/* 8. Triggered By */}
                      <td className="py-3.5 px-4">
                        <span className="inline-flex items-center gap-1 text-slate-600 font-medium">
                          {item.triggeredBy === 'CRON' ? (
                            <Cpu className="w-3.5 h-3.5 text-slate-400" />
                          ) : (
                            <User className="w-3.5 h-3.5 text-slate-400" />
                          )}
                          {item.triggeredBy || 'CRON'}
                        </span>
                      </td>

                      {/* 9. Executed At */}
                      <td className="py-3.5 px-4 text-slate-500 whitespace-nowrap">
                        {formatDate(item.createdAt || item.startedAt)}
                      </td>

                      {/* 10. Action Buttons */}
                      <td className="py-3.5 px-4 text-right whitespace-nowrap">
                        <div className="flex items-center justify-end gap-1.5" onClick={(e) => e.stopPropagation()}>
                          <button
                            onClick={() => handleOpenDetails(item.id)}
                            className="p-1.5 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                            title="View Full Details & Error JSON"
                          >
                            <Eye className="w-4 h-4" />
                          </button>

                          {(isPartial || isFailed) && (
                            <button
                              onClick={(e) => handleRetryLog(item.id, e)}
                              disabled={retryingLogId === item.id}
                              className="px-2.5 py-1 bg-slate-800 hover:bg-slate-900 disabled:opacity-50 text-white rounded text-[11px] font-semibold flex items-center gap-1 transition-colors"
                              title="1-Click Retry"
                            >
                              <RefreshCcw
                                className={`w-3 h-3 ${retryingLogId === item.id ? 'animate-spin' : ''}`}
                              />
                              Retry
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Bar */}
        {pagination.totalPages > 1 && (
          <div className="p-4 border-t border-slate-100 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-slate-500">
            <div>
              Showing page <span className="font-semibold text-slate-700">{pagination.page}</span> of{' '}
              <span className="font-semibold text-slate-700">{pagination.totalPages}</span> (
              {pagination.total} total runs)
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPagination((prev) => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
                disabled={pagination.page <= 1 || loadingLogs}
                className="px-3 py-1.5 bg-white border border-slate-200 rounded-lg font-semibold hover:bg-slate-50 disabled:opacity-40 transition-colors flex items-center gap-1"
              >
                <ChevronLeft className="w-3.5 h-3.5" />
                Previous
              </button>
              <button
                onClick={() =>
                  setPagination((prev) => ({ ...prev, page: Math.min(prev.totalPages, prev.page + 1) }))
                }
                disabled={pagination.page >= pagination.totalPages || loadingLogs}
                className="px-3 py-1.5 bg-white border border-slate-200 rounded-lg font-semibold hover:bg-slate-50 disabled:opacity-40 transition-colors flex items-center gap-1"
              >
                Next
                <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modals & Drawers */}
      <SapLogDetailDrawer
        logId={selectedLogId}
        isOpen={isDetailDrawerOpen}
        onClose={() => {
          setIsDetailDrawerOpen(false);
          setSelectedLogId(null);
        }}
        onRetrySuccess={() => {
          fetchLogs();
          fetchMetrics();
        }}
      />

      <TestSftpModal
        isOpen={isTestSftpOpen}
        onClose={() => setIsTestSftpOpen(false)}
      />

      <TriggerJobModal
        isOpen={isTriggerJobOpen}
        onClose={() => setIsTriggerJobOpen(false)}
        onSuccess={(msg) => {
          showToast(msg || 'Job dispatched successfully!', 'success');
          fetchLogs();
          fetchMetrics();
        }}
      />
    </div>
  );
};

export default SapSyncDashboard;
